import { useCallback, useEffect, useState } from 'react';
import { apiRequest } from '@/lib/api';
import { normalizeRemoteMediaUrl } from '@/lib/media';
import { useAuth } from '@/contexts/AuthContext';
import type { Product } from '@/mocks/admin';

type BackendProduct = {
  id: number;
  nom: string;
  description?: string | null;
  prix: number;
  prix_initial?: number | null;
  admin_profit_fee?: number | null;
  dexpay_payin_fee?: number | null;
  dexpay_payout_fee?: number | null;
  image?: string | null;
  categorie?: string | null;
  vendeur?: string | null;
  badge?: string | null;
  epuise?: boolean;
  stock?: number | null;
  delivery_delay?: string | null;
  delivery_location?: string | null;
  delivery_fee?: number | null;
  delivery_weight?: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'deleted';
  rejection_reason?: string | null;
  user_id?: number | null;
  created_at?: string;
};

const fallbackImage = 'https://readdy.ai/api/search-image?query=product%20item%20clean%20white%20background%20minimal&width=300&height=300&seq=fsn-product-fallback&orientation=squarish';

type ProductInput = Partial<Product> & {
  imageFile?: File | null;
  weight?: string;
};

const normalizeImageUrl = (value?: string | null) => {
  return normalizeRemoteMediaUrl(value, fallbackImage);
};

const imageForPayload = (value?: string | null) => {
  if (!value) return undefined;

  const normalized = value.trim();

  if (
    normalized === fallbackImage ||
    normalized.length > 255 ||
    normalized.includes('readdy.ai/api/search-image') ||
    normalized.startsWith('blob:') ||
    normalized.startsWith('data:') ||
    normalized.startsWith('file:')
  ) {
    return undefined;
  }

  return normalized;
};

const mapProduct = (product: BackendProduct): Product => ({
  id: String(product.id),
  name: product.nom,
  category: product.categorie ?? 'Autres',
  price: Number(product.prix ?? 0),
  originalPrice: product.prix_initial == null ? undefined : Number(product.prix_initial),
  adminProfitFee: product.admin_profit_fee == null ? undefined : Number(product.admin_profit_fee),
  dexpayPayinFee: product.dexpay_payin_fee == null ? undefined : Number(product.dexpay_payin_fee),
  dexpayPayoutFee: product.dexpay_payout_fee == null ? undefined : Number(product.dexpay_payout_fee),
  stock: Number(product.stock ?? 0),
  description: product.description ?? '',
  images: [normalizeImageUrl(product.image)],
  badge: product.badge ?? undefined,
  condition: 'Neuf',
  deliveryDelay: product.delivery_delay ?? 'Non renseigné',
  location: product.delivery_location || 'Non renseigné',
  deliveryFee: Number(product.delivery_fee ?? 0) > 0 ? Number(product.delivery_fee) : 'Gratuit',
  status: product.status,
  vendorId: String(product.user_id ?? ''),
  vendorName: product.vendeur ?? 'Vendeur',
  createdAt: product.created_at ?? new Date().toISOString(),
  rejectionReason: product.rejection_reason ?? undefined,
});

const buildPayload = (product: Product | ProductInput) => {
  const imageFile = 'imageFile' in product ? product.imageFile : null;

  if (imageFile instanceof File) {
    const formData = new FormData();

    if (product.name !== undefined) formData.append('nom', product.name);
    if (product.description !== undefined) formData.append('description', product.description);
    if (product.price !== undefined) formData.append('prix', String(product.price));
    if (product.category !== undefined) formData.append('categorie', product.category);
    if (product.stock !== undefined) formData.append('stock', String(product.stock));
    if (product.badge !== undefined && product.badge !== '') formData.append('badge', product.badge);
    if (product.deliveryDelay !== undefined) formData.append('delivery_delay', product.deliveryDelay);
    if (product.location !== undefined) formData.append('delivery_location', product.location);
    if ('weight' in product && product.weight !== undefined) formData.append('delivery_weight', product.weight);

    formData.append('image_file', imageFile);

    return formData;
  }

  return JSON.stringify({
    nom: product.name,
    description: product.description,
    prix: product.price,
    image: imageForPayload(product.images?.[0]),
    categorie: product.category,
    stock: product.stock,
    badge: product.badge || undefined,
    delivery_delay: product.deliveryDelay,
    delivery_location: product.location,
    delivery_weight: 'weight' in product ? product.weight : undefined,
  });
};

export const useProductsStore = () => {
  const { user, isAuthenticated } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchProducts = useCallback(async () => {
    setLoading(true);

    try {
      let path = '/produits';

      if (isAuthenticated && user?.role === 'admin') {
        path = '/admin/produits';
      } else if (isAuthenticated && user?.role === 'vendor') {
        path = '/vendor/produits';
      }

      const response = await apiRequest<BackendProduct[]>(path);
      setProducts(response.map(mapProduct));
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user?.role]);

  useEffect(() => {
    void fetchProducts();
  }, [fetchProducts]);

  const addProduct = async (product: ProductInput) => {
    await apiRequest('/produits', {
      method: 'POST',
      body: buildPayload(product),
    });

    await fetchProducts();
  };

  const approveProduct = async (id: string) => {
    await apiRequest(`/admin/produits/${id}/approve`, {
      method: 'POST',
    });

    await fetchProducts();
  };

  const rejectProduct = async (id: string, reason: string) => {
    await apiRequest(`/admin/produits/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });

    await fetchProducts();
  };

  const updateProduct = async (id: string, changes: ProductInput) => {
    const body = buildPayload(changes);

    if (body instanceof FormData) {
      body.append('_method', 'PUT');
    }

    await apiRequest(`/produits/${id}`, {
      method: body instanceof FormData ? 'POST' : 'PUT',
      body,
    });

    await fetchProducts();
  };

  const deleteProduct = async (id: string) => {
    await apiRequest(`/produits/${id}`, {
      method: 'DELETE',
    });

    await fetchProducts();
  };

  const restoreProduct = async (id: string) => {
    await apiRequest(`/produits/${id}/restore`, {
      method: 'POST',
    });

    await fetchProducts();
  };

  const getVendorProducts = (vendorId: string) =>
    products.filter((p) => p.vendorId === vendorId);

  return { products, loading, addProduct, approveProduct, rejectProduct, updateProduct, deleteProduct, restoreProduct, getVendorProducts, refreshProducts: fetchProducts };
};
